"use client"

import React, { ChangeEvent, useEffect, useRef, useState } from "react"
import { useRouter } from "next/navigation"
import { addArticleToCurrentBasketAction } from "@/actions/basketActions"
import { loadCardPricesForProductAction } from "@/actions/priceActions"
import { ShoppingCartIcon, TrashIcon } from "lucide-react"
import { useTranslations } from "next-intl"

import { formatPrice } from "@/lib/helper"
import { CardPrices } from "@/lib/price-utils"
import { OrderLineItemData } from "@/lib/storefront/order/order"
import { ReducedProductData } from "@/lib/storefront/product/product"
import { trackAddToBasket } from "@/lib/tracking/tracking-helper"
import { Button } from "@/components/ui/components"
import ImageWithFallback from "@/components/storyblok/common/ImageWithFallback"

interface OrderItem {
  id: string
  title?: string
  brand?: string
  quantity: number
  imageUrl?: string
  article: ReducedProductData
}

interface ReorderOverlayProps {
  lineItems?: OrderLineItemData[]
  isoLocale: string
  customerId: string | undefined
  isB2B: boolean
}

interface ReorderOverlayItemProps {
  item: OrderItem
  customerId: string | undefined
  isoLocale: string
  isB2B: boolean
  onItemChange: (item: OrderItem | null) => void
}

function ReorderOverlayProductItem({ item, isoLocale, isB2B, customerId, onItemChange }: ReorderOverlayItemProps) {
  const t = useTranslations("storefront")
  const [cardPrices, setCardPrices] = React.useState<CardPrices | undefined>(undefined)
  const [localItem, setLocalItem] = useState<OrderItem>(item)

  const increment = () => {
    const updated = { ...localItem, quantity: localItem.quantity + 1 }
    setLocalItem(updated)
    onItemChange(updated.quantity > 0 ? updated : null)
  }

  const decrement = () => {
    const newQuantity = Math.max(0, localItem.quantity - 1)
    const updated = { ...localItem, quantity: newQuantity }
    setLocalItem(updated)
    onItemChange(updated.quantity > 0 ? updated : null)
  }

  const updateQuantity = (value: string) => {
    const quantity = Math.max(0, parseInt(value) || 0)
    const updated = { ...localItem, quantity }
    setLocalItem(updated)
    onItemChange(updated.quantity > 0 ? updated : null)
  }

  useEffect(() => {
    loadCardPricesForProductAction(isoLocale, isB2B, item.article?.ecoTax, item.article.id, customerId).then(
      (loadedCardPrices) => {
        if (loadedCardPrices) {
          //console.dir(loadedCardPrices)
          setCardPrices(loadedCardPrices)
        }
      }
    )
  }, [customerId, item, isB2B, isoLocale])

  return (
    <div className="flex justify-between gap-6 border p-4">
      {/* Left Side: Image, Info, Quantity Controls */}
      <div className="flex flex-1 items-start gap-4">
        <ImageWithFallback
          className={"aspect-square h-20 object-contain"}
          src={localItem.imageUrl ?? ""}
          width={80}
          height={80}
          alt={localItem.title ?? ""}
        />
        <div className="flex flex-col gap-2">
          <div>
            <p className="text-sm font-semibold text-gray-600">{localItem.brand}</p>
            <p className="text-sm font-medium">{localItem.title}</p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={decrement} className="border px-2 py-1">
              -
            </button>
            <input
              type="number"
              value={localItem.quantity}
              min={0}
              onChange={(e: ChangeEvent<HTMLInputElement>) => updateQuantity(e.target.value)}
              className="w-12 border py-1 text-center"
            />
            <button onClick={increment} className="border px-2 py-1">
              +
            </button>
            <Button size={"smNoGrow"} variant={"outline_secondary"} onClick={() => onItemChange(null)} className="">
              <TrashIcon></TrashIcon>
            </Button>
          </div>
        </div>
      </div>

      {/* Vertical divider */}
      <div className="mx-4 w-px bg-gray-300" />

      {/* Right Side: Price info */}
      <div className="flex flex-col items-end justify-between whitespace-nowrap text-sm">
        <div className="text-gray-500">
          {isB2B ? t("price.unit_price_excluding_vat") : t("price.unit_price_including_vat")}
        </div>

        <div className="font-semibold">{formatPrice(isoLocale, cardPrices?.salePrice, cardPrices?.currency)}</div>
        <div className="mt-2 text-gray-500">{isB2B ? t("price.net_amount") : t("price.gross_amount")}</div>
        <div className="font-bold">
          {formatPrice(isoLocale, (cardPrices?.salePrice || 0) * localItem.quantity, cardPrices?.currency)}
        </div>
      </div>
    </div>
  )
}

export default function ReorderOverlay({ lineItems, isoLocale, isB2B, customerId }: ReorderOverlayProps) {
  const [open, setOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const router = useRouter()
  const t = useTranslations("storefront")

  const overlayRef = useRef(null)

  const closeOnOutsideClick = (e: MouseEvent) => {
    if (overlayRef.current && !(overlayRef.current as any).contains(e.target)) {
      setOpen(false)
    }
  }

  const itemsToAdd: OrderItem[] | undefined = lineItems?.map((li) => ({
    id: li.product.id,
    title: li.product.title,
    brand: li.product.brand,
    unitPrice: li.orderLineItem?.pricing?.price?.net,
    quantity: li.orderLineItem.quantity.value,
    imageUrl: li.product.image,
    article: li.product,
  }))
  const [items, setItems] = useState<OrderItem[] | undefined>(itemsToAdd)
  console.log(items, lineItems, itemsToAdd)

  useEffect(() => {
    if (open) {
      document.addEventListener("mousedown", closeOnOutsideClick)
    } else {
      document.removeEventListener("mousedown", closeOnOutsideClick)
    }
    return () => document.removeEventListener("mousedown", closeOnOutsideClick)
  }, [open])

  async function addAllToCart() {
    if (!items) return

    setLoading(true)
    try {
      await Promise.all(
        items.map((item) => {
          return addArticleToCurrentBasketAction(item.id, item.quantity).then((res) => {
            trackAddToBasket(res.isoLocale, res.isB2B, res.product, res.prices, res.quantity)
          })
        })
      )
      router.push("/basket/overview")
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="relative">
      {!open && (
        <Button size={"smNoGrow"} variant={"outline"} className="bg-primary text-white" onClick={() => setOpen(true)}>
          <ShoppingCartIcon strokeWidth={1} className="inline" />
          <span className="ml-2">{t("common.buy_again")}</span>
        </Button>
      )}

      {open && (
        <div className="fixed inset-0 z-[158] flex items-start justify-center overflow-y-auto bg-black/60 sm:items-center">
          <div
            ref={overlayRef}
            className="max-h-[90%] w-full max-w-2xl overflow-y-auto bg-white p-6 shadow-lg sm:mx-auto sm:h-auto sm:w-[90%] sm:max-w-none"
          >
            <div className="mb-6 flex items-center justify-between">
              <h2 className="text-2xl font-bold">{t("common.buy_again")}</h2>
              <button onClick={() => setOpen(false)} className="text-xl font-bold">
                &times;
              </button>
            </div>

            <div className="space-y-4">
              {items?.map((item) => (
                <ReorderOverlayProductItem
                  key={item.id}
                  item={item}
                  isoLocale={isoLocale}
                  isB2B={isB2B}
                  customerId={customerId}
                  onItemChange={(updated) => {
                    setItems((prev) => {
                      if (!updated) return prev?.filter((i) => i.id !== item.id)
                      return prev?.map((i) => (i.id === updated.id ? updated : i))
                    })
                  }}
                />
              ))}
            </div>

            <div className="mt-6 text-right">
              <button
                className="flex items-center justify-center gap-2 bg-primary px-6 py-2 text-white shadow"
                onClick={addAllToCart}
                disabled={loading}
              >
                {loading && <span className="size-4 animate-spin border-t-2 border-white border-opacity-50"></span>}
                {loading ? t("product.is_adding") : t("product.add_to_basket")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
